import { Link, useLocation } from "react-router-dom";
import { KeyRound, LogOut, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { useState } from "react";
import type { NavItem } from "@/types/site-config";

const DEFAULT_NAV_LINKS: NavItem[] = [
  { href: "/", label: "Home" },
  { href: "/galleries", label: "Photos" },
  { href: "/blog", label: "Writing" },
  { href: "/links", label: "Links" },
  { href: "/audio", label: "Audio" },
  { href: "/archive", label: "Archive" },
  { href: "/about", label: "About" },
];

function getNavLinkClass(pathname: string, href: string): string {
  return cn(
    "relative font-mono text-sm font-medium transition-colors hover:text-link",
    pathname === href
      ? "text-link after:absolute after:bottom-[-1.25rem] after:left-0 after:right-0 after:h-0.5 after:bg-link"
      : "text-secondary"
  );
}

function getAdminLinkClass(pathname: string): string {
  return cn(
    "relative font-mono text-sm font-medium transition-colors hover:text-link",
    pathname.startsWith("/admin")
      ? "text-link after:absolute after:bottom-[-1.25rem] after:left-0 after:right-0 after:h-0.5 after:bg-link"
      : "text-secondary"
  );
}

function getMobileNavLinkClass(pathname: string, href: string): string {
  return cn(
    "rounded px-4 py-3 font-mono text-base font-medium transition-colors",
    pathname === href
      ? "bg-link/10 text-link"
      : "text-secondary hover:bg-secondary/20 hover:text-link"
  );
}

function getMobileAdminLinkClass(pathname: string): string {
  return cn(
    "rounded px-4 py-3 font-mono text-base font-medium transition-colors",
    pathname.startsWith("/admin")
      ? "bg-link/10 text-link"
      : "text-secondary hover:bg-secondary/20 hover:text-link"
  );
}

function AuthIcon({ isAuthenticated }: { isAuthenticated: boolean }) {
  return isAuthenticated ? (
    <LogOut className="h-5 w-5" />
  ) : (
    <KeyRound className="h-5 w-5" />
  );
}

async function clearFlaskJwt(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch (error) {
    console.error('Failed to clear Flask JWT:', error);
  }
}

async function submitAuthForm(action: 'signin' | 'signout'): Promise<void> {
  const csrfRes = await fetch('/api/auth/csrf');
  const { csrfToken } = await csrfRes.json();

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action === 'signin' ? '/api/auth/signin/authentik' : '/api/auth/signout';

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'csrfToken';
  input.value = csrfToken;
  form.appendChild(input);

  const callbackInput = document.createElement('input');
  callbackInput.type = 'hidden';
  callbackInput.name = 'callbackUrl';
  callbackInput.value = '/';
  form.appendChild(callbackInput);

  document.body.appendChild(form);
  form.submit();
}

function getAuthLabel(isAuthenticated: boolean): string {
  return isAuthenticated ? "Sign out" : "Sign in";
}

function getAuthAction(isAuthenticated: boolean): 'signin' | 'signout' {
  return isAuthenticated ? 'signout' : 'signin';
}

function DesktopNav({ pathname, isAuthenticated, isLoading, onAuthAction, navLinks }: { pathname: string; isAuthenticated: boolean; isLoading: boolean; onAuthAction: (action: 'signin' | 'signout') => void; navLinks: NavItem[] }) {
  return (
    <nav className="hidden items-center gap-8 md:flex">
      {navLinks.map((link) => (
        <Link key={link.href} to={link.href} className={getNavLinkClass(pathname, link.href)}>
          {link.label}
        </Link>
      ))}
      {isAuthenticated && (
        <Link to="/admin" className={getAdminLinkClass(pathname)}>Admin</Link>
      )}
      <div className="h-6 w-px bg-border" />
      <ThemeSwitcher />
      {!isLoading && (
        <button
          onClick={() => onAuthAction(getAuthAction(isAuthenticated))}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          title={getAuthLabel(isAuthenticated)}
          aria-label={getAuthLabel(isAuthenticated)}
        >
          <AuthIcon isAuthenticated={isAuthenticated} />
        </button>
      )}
    </nav>
  );
}

function MobileNav({ pathname, isAuthenticated, isLoading, onClose, onAuthAction, navLinks }: { pathname: string; isAuthenticated: boolean; isLoading: boolean; onClose: () => void; onAuthAction: (action: 'signin' | 'signout') => void; navLinks: NavItem[] }) {
  // Header is `sticky top-0` with a 4rem-tall bar. The menu renders inside the
  // header, so it can't grow past the viewport without becoming unreachable —
  // bottom items (Sign in) get clipped on small screens. Cap the menu to the
  // small-viewport height minus the bar (svh accounts for mobile browser
  // chrome staying visible) and give it its own scroll. overscroll-contain
  // keeps wheel/touch scrolling inside the menu instead of bleeding to the
  // page underneath.
  return (
    <div className="max-h-[calc(100svh-4rem)] overflow-y-auto overscroll-contain border-t border-divider bg-background md:hidden">
      <nav className="container flex flex-col space-y-1 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {navLinks.map((link) => (
          <Link key={link.href} to={link.href} onClick={onClose} className={getMobileNavLinkClass(pathname, link.href)}>
            {link.label}
          </Link>
        ))}
        {isAuthenticated && (
          <Link to="/admin" onClick={onClose} className={getMobileAdminLinkClass(pathname)}>Admin</Link>
        )}
        <div className="pt-2">
          <ThemeSwitcher />
        </div>
        {!isLoading && (
          <div className="pt-2">
            <button
              onClick={() => onAuthAction(getAuthAction(isAuthenticated))}
              className="flex w-full items-center justify-center rounded border-2 border-foreground bg-transparent px-4 py-3 font-mono text-sm uppercase tracking-wider text-foreground transition-all hover:bg-foreground/10 min-h-[48px]"
              title={getAuthLabel(isAuthenticated)}
              aria-label={getAuthLabel(isAuthenticated)}
            >
              <AuthIcon isAuthenticated={isAuthenticated} />
            </button>
          </div>
        )}
      </nav>
    </div>
  );
}

function resolveNavLinks(config: ReturnType<typeof useSiteConfig>['data']): NavItem[] {
  return config?.navigation ?? DEFAULT_NAV_LINKS;
}

function resolveLogoSrc(config: ReturnType<typeof useSiteConfig>['data']): string {
  const fallback = config?.branding?.logoFallback ?? '/logo-symbol.png';
  return config?.site.logo ?? `${import.meta.env.VITE_CDN_URL ?? ''}${fallback}`;
}

const Header = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { data: config } = useSiteConfig();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = resolveNavLinks(config);
  const logoSrc = resolveLogoSrc(config);

  const handleAuthAction = async (action: 'signin' | 'signout') => {
    setMobileMenuOpen(false);
    if (action === 'signout') {
      await clearFlaskJwt();
    }
    await submitAuthForm(action);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-divider bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="group flex items-center transition-opacity hover:opacity-90" aria-label="Home">
          {config?.branding?.showSiteTitle === true && config?.site?.name && (
            <span className="font-serif text-2xl font-semibold leading-none text-foreground">
              {config.site.name}
            </span>
          )}
        </Link>
        <DesktopNav pathname={location.pathname} isAuthenticated={isAuthenticated} isLoading={isLoading} onAuthAction={handleAuthAction} navLinks={navLinks} />
        <button
          className="flex items-center justify-center p-2 text-foreground transition-colors hover:text-link md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {mobileMenuOpen && (
        <MobileNav pathname={location.pathname} isAuthenticated={isAuthenticated} isLoading={isLoading} onClose={() => setMobileMenuOpen(false)} onAuthAction={handleAuthAction} navLinks={navLinks} />
      )}
    </header>
  );
};

export default Header;
